import { ReactNode } from 'react';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import { useLanguage } from '../../../../../context/LanguageContext';

interface EditorFooterProps {
  alerts: ReactNode;
  canGoBack: boolean;
  onBack: () => void;
  onSaveDraft: () => void;
  savingDraft: boolean;
  terminating: boolean;
  creating: boolean;
  isLastStep: boolean;
  onNext: () => void;
  launchingCount: boolean;
  onTerminate: () => void;
  terminateBlockedReason: string | null;
  // Raison qui empêche de passer à l'étape suivante (ex. aucun canal choisi), affichée au survol
  nextBlockedReason?: string | null;
}

const spinner = <CircularProgress size={14} color="inherit" />;

function EditorFooter({
  alerts, canGoBack, onBack, onSaveDraft, savingDraft, terminating, creating, isLastStep, onNext, launchingCount, onTerminate, terminateBlockedReason,
  nextBlockedReason = null,
}: EditorFooterProps) {
  const { t } = useLanguage();
  const busy = creating || savingDraft || terminating;

  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'grey.200', px: 3, py: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'stretch', flexShrink: 0 }}>
      {alerts}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        {canGoBack && (
          <Button variant="text" size="small" onClick={onBack} sx={{ color: 'text.secondary', textTransform: 'none' }}>
            Précédent
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={savingDraft ? spinner : <SaveIcon />}
          onClick={onSaveDraft}
          disabled={busy}
          sx={{ color: 'text.secondary', borderColor: 'divider', textTransform: 'none', fontWeight: 500 }}
        >
          Enregistrer le brouillon
        </Button>
        {isLastStep ? (
          <Tooltip title={terminateBlockedReason ?? ''} disableHoverListener={!terminateBlockedReason}>
            <span>
              <Button
                variant="outlined"
                startIcon={terminating ? spinner : undefined}
                onClick={onTerminate}
                disabled={busy || terminateBlockedReason !== null}
                sx={{ color: 'primary.main', borderColor: 'primary.main', textTransform: 'none', fontWeight: 600 }}
              >
                Terminer
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={nextBlockedReason ?? ''} disableHoverListener={!nextBlockedReason}>
            <span>
              <Button
                variant="contained"
                endIcon={creating || launchingCount ? spinner : <ArrowForwardIcon />}
                onClick={onNext}
                disabled={busy || launchingCount || nextBlockedReason !== null}
                sx={{
                  backgroundColor: 'primary.main', textTransform: 'none', fontWeight: 600,
                  '&:hover': { backgroundColor: 'primary.main', opacity: 0.9 },
                  '&.Mui-disabled': { backgroundColor: 'grey.200', color: 'text.disabled' },
                }}
              >
                {t('pages.marketing.communication.nextStep')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

export default EditorFooter;
